// ---------------------------------------------------------------------------
// OverviewV3 — matriz heatmap "input × mês" com colunas fixas.
// ---------------------------------------------------------------------------
// Porta para React a variação "O" (heatmap com agrupamento vertical)
// validada em `sandbox/inputs_v3_overview_and_monthpicker.html`.
//
// Premissas do design:
// - Duas colunas à esquerda ficam fixas (input, responsável) — assim
//   como a coluna do título do grupo ocupa full-width sticky nessa
//   faixa fixa. Só a área dos meses rola horizontalmente.
// - Cabeçalho de duas linhas: "ano" (colspan dos meses do ano) + "mês"
//   (label de 3 letras). Separador visual discreto entre anos.
// - Cada célula-mês é um quadradinho colorido (waiting/action/done/
//   stale) com tooltip no hover; clique abre o drawer daquele input
//   naquele mês (se for mês válido — futuro fica tracejado).
// - Filtro por responsável como único filtro (a view principal já
//   oferece filtros por estado via badges).
//
// Integração:
// - Status real por (input, mês) vem do prop `inputsStateByPeriod`
//   (servido pelo `progress_overview` do backend). Cores espelham a
//   paleta do `InputBoxV3` via `classifyInputFromRuntimeState`.
// - Enquanto o backend não responde, a célula renderiza um SKELETON
//   shimmer animado — nunca dado simulado.
// - O mês selecionado é apenas uma referência (não "destaca"); o
//   clique num input-mês distinto abre o drawer daquele input no mês
//   correspondente — a view pai decide se precisa alinhar
//   `selectedMonth` ou emitir um warning.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import ReactSelect, { components as RSComponents } from "react-select";
import Tooltip from "@material-ui/core/Tooltip";
import Repeat from "@material-ui/icons/Repeat";
import PersonOutline from "@material-ui/icons/PersonOutline";
import FlashOn from "@material-ui/icons/FlashOn";
import TrendingFlat from "@material-ui/icons/TrendingFlat";
import Event from "@material-ui/icons/Event";
import HelpOutline from "@material-ui/icons/HelpOutline";

// Override do Input do react-select usado pelo filtro de responsavel.
// O Chrome moderno ignora ``autoComplete="off"`` e mesmo
// ``"new-password"`` na heuristica de "nome/email" quando o input
// esta proximo de campos com aria-label/placeholder em PT/EN/ES.
// Combinacao definitiva (mesma do TextField de busca em InputsV3.js):
//   - ``readOnly`` no mount + libera no primeiro focus (Chrome nao
//     reativa autofill apos a primeira edicao manual)
//   - ``name`` aleatorio
//   - atributos especiais ignorados por LastPass/1Password
//   - honeypot ``<input>`` invisivel renderizado ANTES do select
//     (declarado inline no JSX abaixo)
const NoAutofillInput = ({ onFocus, ...rest }) => {
  const randomName = React.useMemo(
    () => `overview-v3-resp-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );
  return (
    <RSComponents.Input
      {...rest}
      name={randomName}
      autoComplete="new-password"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      readOnly
      onFocus={(e) => {
        e.target.removeAttribute("readonly");
        if (typeof onFocus === "function") onFocus(e);
      }}
      data-lpignore="true"
      data-form-type="other"
      data-1p-ignore="true"
      aria-autocomplete="none"
      role="presentation"
    />
  );
};

NoAutofillInput.propTypes = {
  onFocus: PropTypes.func,
};

import {
  grayColor,
  whiteColor,
} from "../../../assets/jss/material-dashboard-pro-react";
import { stageFlatColor } from "./StagePips";
import {
  classifyMonthAggregate,
  monthStateLabel,
  INPUT_STATE_BACKGROUNDS,
  inputStateLabel,
  PROCESSING_COLORS,
} from "../../../services/inputsV3StateRules";
import { classifyInputFromRuntimeState } from "../../../services/inputsV3Adapter";
import {
  isCarryForward,
  isSingleton,
  isYearly,
} from "../../../services/inputsV3Volatility";
import { InputsLegendPill } from "./InputsLegend";

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

const MONTH_ABBR = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

const MONTHS_PT_LONG = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

function groupByYear(monthlyStatus) {
  const out = [];
  monthlyStatus.forEach((m, i) => {
    const last = out[out.length - 1];
    if (last && last.year === m.year) last.items.push({ month: m, idx: i });
    else out.push({ year: m.year, items: [{ month: m, idx: i }] });
  });
  return out;
}

// ----------------------------------------------------------------------
// Styles — espelha as regras .ov-* do protótipo HTML.
// ----------------------------------------------------------------------

const COL_INPUT_WIDTH = 250;
const COL_RESP_WIDTH = 200;
const STICKY_BG = whiteColor;
const HEADER_BG = grayColor[13]; // #f5f5f5
const BORDER_COLOR = grayColor[8]; // #eeeeee
const BORDER_YEAR = grayColor[5]; // #DDD

const useStyles = makeStyles(() => ({
  filters: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "12px",
  },
  // (legenda extraida para ``InputsLegend`` — componente compartilhado.)
  filterLabel: {
    // Igual ao label "Organizar por" da view principal
    // (``InputsV3.js`` -> sortToggleBar) para consistência visual.
    fontSize: "0.72rem",
    color: grayColor[6],
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    paddingLeft: "2px",
    whiteSpace: "nowrap",
    textTransform: "none",
    letterSpacing: "0.02em",
  },
  wrap: {
    overflowX: "auto",
    overflowY: "hidden",
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: "6px",
    background: whiteColor,
    scrollbarWidth: "thin",
    scrollbarColor: `${grayColor[9]} transparent`,
    "&::-webkit-scrollbar": { height: "8px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: grayColor[9],
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": { background: grayColor[1] },
  },
  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100%",
    "& thead th": {
      background: HEADER_BG,
      fontWeight: 500,
      color: grayColor[0],
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      fontSize: "10px",
      padding: "8px 4px",
      borderBottom: `1px solid ${BORDER_COLOR}`,
      position: "sticky",
      top: 0,
      zIndex: 2,
    },
    "& thead .ov-row-year th": {
      padding: "8px 6px 6px",
      borderBottom: `1px solid ${BORDER_COLOR}`,
      color: grayColor[1],
      fontSize: "11px",
    },
    "& thead .ov-row-year th.year-sep": {
      borderLeft: `2px solid ${BORDER_YEAR}`,
    },
    "& thead .ov-row-month th": {
      padding: "6px 2px 10px",
      fontSize: "10px",
    },
    "& thead .ov-row-month th.year-sep-m": {
      borderLeft: `2px solid ${BORDER_YEAR}`,
    },
    // Colunas fixas — sticky à esquerda
    "& th.col-input, & td.cell-input": {
      position: "sticky",
      left: 0,
      zIndex: 2,
      background: STICKY_BG,
    },
    "& thead th.col-input": { background: HEADER_BG, zIndex: 4 },
    "& th.col-resp, & td.cell-resp": {
      position: "sticky",
      left: COL_INPUT_WIDTH,
      zIndex: 2,
      background: STICKY_BG,
    },
    "& thead th.col-resp": {
      background: HEADER_BG,
      zIndex: 4,
      boxShadow: "2px 0 4px -2px rgba(0,0,0,0.12)",
    },
    "& th.col-input": {
      textAlign: "left",
      paddingLeft: "14px",
      width: COL_INPUT_WIDTH,
      minWidth: COL_INPUT_WIDTH,
      borderRight: `1px solid ${BORDER_COLOR}`,
    },
    "& th.col-resp": {
      textAlign: "left",
      paddingLeft: "10px",
      width: COL_RESP_WIDTH,
      minWidth: COL_RESP_WIDTH,
      borderRight: `1px solid ${BORDER_COLOR}`,
    },
    "& td": {
      padding: "2px 2px",
      borderBottom: `1px solid ${BORDER_COLOR}`,
    },
    "& tr:last-child td": { borderBottom: "none" },
    "& td.cell-input": {
      fontSize: "12.5px",
      color: grayColor[2],
      paddingLeft: "28px",
      borderRight: `1px solid ${BORDER_COLOR}`,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: COL_INPUT_WIDTH,
    },
    "& td.cell-resp": {
      paddingLeft: "10px",
      borderRight: `1px solid ${BORDER_COLOR}`,
      boxShadow: "2px 0 4px -2px rgba(0,0,0,0.12)",
      // ``maxWidth`` na celula trava a largura para que o
      // ``text-overflow: ellipsis`` no ``.respName`` interno tenha
      // contexto. Sem isso a celula se expande conforme o conteudo
      // (cell-table default) e o ellipsis nunca dispara.
      width: COL_RESP_WIDTH,
      maxWidth: COL_RESP_WIDTH,
    },
    "& td.year-sep-m": { borderLeft: `2px solid ${BORDER_YEAR}` },
  },
  groupRow: {
    "& td": {
      background: HEADER_BG,
      fontSize: "11px",
      fontWeight: 500,
      color: grayColor[1],
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      padding: "10px 14px",
      borderTop: `1px solid ${BORDER_YEAR}`,
      borderBottom: `1px solid ${BORDER_COLOR}`,
    },
    "& td.ov-group-left": {
      position: "sticky",
      left: 0,
      zIndex: 3,
      borderRight: `1px solid ${BORDER_COLOR}`,
      boxShadow: "2px 0 4px -2px rgba(0,0,0,0.12)",
    },
    "& td.ov-group-right": { padding: 0 },
  },
  groupCount: {
    marginLeft: "8px",
    color: grayColor[0],
    fontWeight: 400,
    letterSpacing: "0.04em",
  },
  // Coluna ``Responsavel`` — apenas a lista de nomes em CSV. Quando
  // o texto excede ``COL_RESP_WIDTH``, recebe ellipsis. ``minWidth:
  // 0`` no flex container e' essencial pra o ellipsis funcionar
  // dentro de um ``inline-flex``.
  resp: {
    display: "inline-flex",
    alignItems: "center",
    minWidth: 0,
    maxWidth: "100%",
  },
  respName: {
    fontSize: "12px",
    color: grayColor[1],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    flex: 1,
  },
  // Badge "input reutilizavel" — exibido apos o nome do input quando
  // ``volatility`` deixa uma entrega cobrir varios meses (singleton,
  // semi_fixed, carry_forward, yearly — qualquer um menos
  // ``monthly``). Icone discreto (12px), sem texto, com tooltip.
  reuseBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    background: grayColor[12],
    color: grayColor[2],
    marginLeft: "6px",
    flexShrink: 0,
    verticalAlign: "middle",
    "& svg": { fontSize: "11px" },
  },
  inputNameWrap: {
    display: "inline-flex",
    alignItems: "center",
    minWidth: 0,
    width: "100%",
  },
  inputNameText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  cell: {
    width: "22px",
    height: "20px",
    borderRadius: "3px",
    margin: "0 auto",
    cursor: "pointer",
    transition: "transform 140ms ease, box-shadow 140ms ease",
    "&:hover": {
      transform: "scale(1.2)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
    },
  },
  cellFuture: {
    background:
      "repeating-linear-gradient(45deg, #f5f5f5, #f5f5f5 2px, #ebebeb 2px, #ebebeb 4px)",
    cursor: "default",
    "&:hover": { transform: "none", boxShadow: "none" },
  },
  // Placeholder animado exibido enquanto o backend ainda nao
  // devolveu ``inputs_state_by_period`` (ou ``month_aggregates``).
  // Substitui o antigo fallback ``statusForMock`` — mostrar dados
  // simulados durante o carregamento confunde o usuario, ja que a
  // cor ``done`` nao tem nenhuma relacao com o estado real.
  // O efeito shimmer e' um gradiente que desliza horizontalmente,
  // padrao consagrado de skeleton-loading. Mantemos o tamanho e
  // border-radius da ``cell`` real para que a transicao "skeleton ->
  // dado real" nao cause layout shift.
  "@keyframes overviewShimmer": {
    "0%": { backgroundPosition: "-120px 0" },
    "100%": { backgroundPosition: "120px 0" },
  },
  cellSkeleton: {
    width: "22px",
    height: "20px",
    borderRadius: "3px",
    margin: "0 auto",
    cursor: "default",
    background: `linear-gradient(90deg, ${grayColor[10]} 0%, ${grayColor[12]} 50%, ${grayColor[10]} 100%)`,
    backgroundSize: "240px 100%",
    animation: "$overviewShimmer 1.4s linear infinite",
    "&:hover": { transform: "none", boxShadow: "none" },
  },
  // Estado real "processando" (algo acontecendo neste instante) usa
  // o mesmo shimmer cinza do skeleton, mas continua sendo uma celula
  // clicavel com tooltip — diferente do loading inicial, que nao tem
  // dado real ainda.
  cellProcessing: {
    background: `linear-gradient(90deg, ${PROCESSING_COLORS.base} 0%, ${PROCESSING_COLORS.highlight} 50%, ${PROCESSING_COLORS.base} 100%)`,
    backgroundSize: "240px 100%",
    animation: "$overviewShimmer 1.4s linear infinite",
  },
  // Linha 0 da tabela — "Progresso da modelagem" do mes. Reusa a
  // mesma ``classes.cell`` quadrada das celulas de input (com hover
  // de scale embutido) — esta linha so adiciona o fundo destacado
  // do header e o label sticky a esquerda.
  monthStatusRow: {
    "& td": {
      background: HEADER_BG,
      borderBottom: `1px solid ${BORDER_YEAR}`,
      padding: "6px 4px",
    },
  },
  monthStatusLabelCell: {
    position: "sticky",
    left: 0,
    zIndex: 3,
    borderRight: `1px solid ${BORDER_COLOR}`,
    boxShadow: "2px 0 4px -2px rgba(0,0,0,0.12)",
    // Fonte equalizada aos dividers de grupo (``groupRow > td``)
    // logo abaixo: mesma familia (uppercase, letter-spacing 0.08em),
    // mesmo peso/cor, mesmo size 11px. Garante leitura visual de
    // que "Progresso da modelagem" e' do MESMO nivel hierarquico
    // que os agrupadores de inputs.
    color: grayColor[1],
    fontSize: "11px",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    paddingLeft: "14px !important",
  },
  monthStatusCell: {
    textAlign: "center",
  },
  tooltip: {
    position: "fixed",
    background: "#1f2937",
    color: "#fff",
    fontSize: "11px",
    padding: "8px 10px",
    borderRadius: "4px",
    pointerEvents: "none",
    zIndex: 30,
    maxWidth: "260px",
    lineHeight: 1.45,
    boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
    opacity: 0,
    transform: "translateY(-4px)",
    transition: "opacity 180ms ease, transform 180ms ease",
    "&.on": { opacity: 1, transform: "translateY(0)" },
    "& strong": { color: "#fff", fontWeight: 500 },
  },
}));

// ----------------------------------------------------------------------
// Componente principal
// ----------------------------------------------------------------------

function OverviewV3({
  inputsStatus,
  monthlyStatus,
  // ``selectedMonth`` permanece no propTypes (callers passam) — nao
  // consumido aqui apos a remocao do click no nome do input. Manter
  // como destructure ignorado para nao quebrar o contrato externo.
  // eslint-disable-next-line no-unused-vars
  selectedMonth,
  onOpenInput,
  onOpenMonthProgress,
  selectStyles,
  inputsStateByPeriod,
}) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const wrapRef = useRef(null);
  const tooltipRef = useRef(null);
  const [respFilter, setRespFilter] = useState(null);

  // `selectStyles` herdado da view pai carrega `control.marginTop: -12px`
  // para compensar labels flutuantes de outros usos. Aqui o select vive
  // sozinho dentro do filter bar — sobrescrevemos localmente para ficar
  // ancorado pelo top do container.
  const mergedSelectStyles = useMemo(() => {
    const base = selectStyles || {};
    return {
      ...base,
      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        marginTop: 0,
      }),
    };
  }, [selectStyles]);

  const grouped = useMemo(() => groupByYear(monthlyStatus), [monthlyStatus]);

  // Lookup ``(year, month, meta_name) -> bucket`` (categoria de cor do
  // InputBox). Construido a partir de ``inputsStateByPeriod`` (vindo
  // do ``progress_overview`` no backend). Quando ``undefined``, o
  // backend ainda nao retornou — renderizamos skeleton animado.
  // Quando array vazio ou cell sem entrada, caimos em ``not_sent``
  // (cinza neutro) — NUNCA mock, que confunde o usuario com cores
  // fantasiosas durante o load.
  const isLoadingInputs = inputsStateByPeriod === undefined;
  const stateBucketByCell = useMemo(() => {
    const map = new Map();
    if (Array.isArray(inputsStateByPeriod)) {
      for (const row of inputsStateByPeriod) {
        if (!row || !row.meta_name) continue;
        const key = `${row.year}-${row.month}-${row.meta_name}`;
        map.set(key, classifyInputFromRuntimeState(row));
      }
    }
    return map;
  }, [inputsStateByPeriod]);

  // Lista única de responsáveis → alimenta o select de filtro.
  // Considera ambos os shapes coexistentes: `responsible_users` (V3) e
  // `responsible` (V2) — alinhado ao helper `responsibleList()` usado
  // no InputBoxV3.
  const responsibleOptions = useMemo(() => {
    const map = new Map();
    (inputsStatus || []).forEach((g) => {
      (g.inputs || []).forEach((inp) => {
        const list = Array.isArray(inp.responsible_users)
          ? inp.responsible_users
          : Array.isArray(inp.responsible)
            ? inp.responsible
            : [];
        list.forEach((r) => {
          if (r && r.id && !map.has(r.id))
            map.set(r.id, { value: r.id, label: r.name });
        });
      });
    });
    return Array.from(map.values());
  }, [inputsStatus]);

  // Na primeira renderização, posiciona scroll no extremo direito —
  // comportamento esperado (mês mais recente deve estar visível).
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    wrap.scrollLeft = wrap.scrollWidth - wrap.clientWidth;
  }, [monthlyStatus.length]);

  if (!monthlyStatus || monthlyStatus.length === 0) return null;
  if (!inputsStatus || inputsStatus.length === 0) return null;

  const totalCols = 2 + monthlyStatus.length;

  // Label do mês para cabeçalho (ex.: "Jun"). Usa o dicionário V3 com
  // fallback para PT-BR — mesmo padrão do MonthSelectorV3.
  const monthShort = (monthNum) => {
    const key = MONTH_ABBR[monthNum - 1];
    const translated = i18n.exists?.(`common.months.${key}`)
      ? i18n.t(`common.months.${key}`)
      : null;
    const base =
      translated ||
      // Fallback agnóstico ao idioma.
      MONTHS_PT_LONG[monthNum - 1] ||
      key;
    const short = base.slice(0, 3);
    return short.charAt(0).toUpperCase() + short.slice(1);
  };

  const monthLong = (monthNum) => {
    const key = MONTH_ABBR[monthNum - 1];
    const translated = i18n.exists?.(`common.months.${key}`)
      ? i18n.t(`common.months.${key}`)
      : null;
    return translated || MONTHS_PT_LONG[monthNum - 1] || "";
  };

  // Normaliza a lista de responsáveis de um input (V3/V2/legacy).
  const inputResponsibles = (inp) =>
    Array.isArray(inp?.responsible_users)
      ? inp.responsible_users
      : Array.isArray(inp?.responsible)
        ? inp.responsible
        : [];

  const filteredGroups = (inputsStatus || [])
    .map((g) => {
      const respId = respFilter?.value;
      const inputs = (g.inputs || []).filter((inp) => {
        if (!respId) return true;
        const ids = inputResponsibles(inp).map((r) => r.id);
        return ids.includes(respId);
      });
      return { ...g, inputs };
    })
    .filter((g) => g.inputs.length > 0);

  // Posicionamento do tooltip com clamping/flip nas bordas da
  // viewport. Preferencia: canto inferior-direito do cursor (12px de
  // offset). Se o tooltip estouraria a direita/baixo, vira para o
  // lado oposto; em ultimo caso (mesmo o flip estoura), e' clampado
  // no padding minimo (``MARGIN``). Mantemos ``position: fixed`` no
  // CSS — coordenadas de viewport, sem precisar considerar scroll
  // do container do overview.
  //
  // Re-medimos o ``getBoundingClientRect`` no ``showTooltip`` apos
  // setar o innerHTML (conteudo varia entre celulas: input vs mes,
  // textos com tamanhos diferentes). No ``moveTooltip`` o conteudo
  // nao muda mais, mas re-medimos mesmo assim — barato e blindado
  // contra reflow tardio (ex.: web fonts carregam depois).
  const TOOLTIP_OFFSET = 12;
  const TOOLTIP_VIEWPORT_MARGIN = 8;
  const placeTooltip = (e) => {
    const tt = tooltipRef.current;
    if (!tt) return;
    const rect = tt.getBoundingClientRect();
    const w = rect.width || 0;
    const h = rect.height || 0;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Padrao: abaixo + a direita do cursor.
    let left = e.clientX + TOOLTIP_OFFSET;
    let top = e.clientY + TOOLTIP_OFFSET;
    // Flip horizontal se estouraria pela direita.
    if (left + w + TOOLTIP_VIEWPORT_MARGIN > vw) {
      left = e.clientX - w - TOOLTIP_OFFSET;
      if (left < TOOLTIP_VIEWPORT_MARGIN) left = TOOLTIP_VIEWPORT_MARGIN;
    }
    // Flip vertical se estouraria por baixo.
    if (top + h + TOOLTIP_VIEWPORT_MARGIN > vh) {
      top = e.clientY - h - TOOLTIP_OFFSET;
      if (top < TOOLTIP_VIEWPORT_MARGIN) top = TOOLTIP_VIEWPORT_MARGIN;
    }
    tt.style.left = left + "px";
    tt.style.top = top + "px";
  };
  const showTooltip = (e, html) => {
    const tt = tooltipRef.current;
    if (!tt) return;
    tt.innerHTML = html;
    tt.classList.add("on");
    placeTooltip(e);
  };
  const moveTooltip = (e) => {
    placeTooltip(e);
  };
  const hideTooltip = () => {
    const tt = tooltipRef.current;
    if (!tt) return;
    tt.classList.remove("on");
  };

  const displayName = (input) =>
    (input?.input_name && input.input_name[lang]) ||
    input?.input_name?.pt ||
    input?.meta_name ||
    "—";

  const renderThead = () => (
    <thead>
      <tr className="ov-row-year">
        <th
          className="col-input"
          rowSpan="2"
          style={{ verticalAlign: "bottom" }}
        >
          {t("inputs_v3.overview.col_input", "Input")}
        </th>
        <th
          className="col-resp"
          rowSpan="2"
          style={{ verticalAlign: "bottom" }}
        >
          {t("inputs_v3.overview.col_responsible", "Responsável")}
        </th>
        {grouped.map((y, idx) => (
          <th
            key={`y-${y.year}`}
            className={`year-col${idx > 0 ? " year-sep" : ""}`}
            colSpan={y.items.length}
          >
            {y.year}
          </th>
        ))}
      </tr>
      <tr className="ov-row-month">
        {grouped.map((y, yIdx) =>
          y.items.map(({ month, idx: mIdx }, j) => {
            const sep = yIdx > 0 && j === 0 ? " year-sep-m" : "";
            return (
              <th key={`m-${mIdx}`} className={`col-month${sep}`}>
                {monthShort(month.month)}
              </th>
            );
          }),
        )}
      </tr>
    </thead>
  );

  return (
    <div>
      <div className={classes.filters}>
        <span className={classes.filterLabel}>
          {t(
            "inputs_v3.overview.filter_by_responsible",
            "Filtrar por responsável:",
          )}
        </span>
        <div style={{ minWidth: 220, position: "relative" }}>
          {/* Honeypot anti-autofill: campo "fake" invisivel renderizado
              ANTES do react-select para que o navegador (Chrome / Edge
              / 1Password / LastPass) escolha ESTE para preencher com
              o nome/email do usuario logado, em vez do input real do
              filtro. Mesmo truque consagrado usado no TextField de
              busca em ``InputsV3.js``. O override ``NoAutofillInput``
              (acima) ja cobre os atributos do input do react-select
              (readOnly ate o primeiro focus, name aleatorio,
              data-lpignore, data-1p-ignore, etc.); o honeypot e' a
              ultima linha de defesa contra heuristicas que ignoram
              esses atributos. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            readOnly
            style={{
              position: "absolute",
              left: -10000,
              top: -10000,
              width: 1,
              height: 1,
              opacity: 0,
            }}
          />
          <ReactSelect
            styles={mergedSelectStyles}
            isClearable
            placeholder={t("inputs_v3.overview.filter_all", "todos")}
            options={responsibleOptions}
            value={respFilter}
            onChange={setRespFilter}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            inputId={`overview-v3-resp-${Math.random().toString(36).slice(2, 8)}`}
            name={`overview-v3-resp-${Math.random().toString(36).slice(2, 8)}`}
            components={{ Input: NoAutofillInput }}
          />
        </div>
        {/* Rev. 2026-05-26: legenda do Overview migrada de
            componente standalone (faixa larga abaixo dos filtros)
            para o mesmo pill+popover da visao detalhada. ``marginLeft:
            auto`` empurra para a borda direita do card de filtros. */}
        <div style={{ marginLeft: "auto" }}>
          <InputsLegendPill placement="bottom-end" />
        </div>
      </div>

      <div className={classes.wrap} ref={wrapRef}>
        <table className={classes.table}>
          {renderThead()}
          <tbody>
            {/* Linha 0 da tabela — "Progresso da modelagem" do mes.
                Reusa a MESMA caixinha quadrada (``classes.cell``) das
                celulas de input abaixo, mas com a paleta do seletor
                de meses (``classifyMonthAggregate`` + ``stageFlatColor``
                — exatamente as cores dos tracinhos sob cada tab no
                ``MonthSelectorV3``). Click abre o ``ProgressDrawerV3``
                no mes correspondente. */}
            <tr className={classes.monthStatusRow}>
              <td colSpan="2" className={classes.monthStatusLabelCell}>
                {t(
                  "inputs_v3.overview.modeling_progress",
                  "Progresso da modelagem",
                )}
              </td>
              {grouped.map((y, yIdx) =>
                y.items.map(({ month, idx: mIdx }, j) => {
                  const sep = yIdx > 0 && j === 0 ? " year-sep-m" : "";
                  const state = classifyMonthAggregate(month.status);
                  // ``state === "loading"`` significa ``month.status``
                  // ainda nulo (progress_overview sem resposta). Mostra
                  // skeleton no lugar de uma cor "fingida".
                  if (state === "loading") {
                    return (
                      <td
                        key={`mo-st-${mIdx}`}
                        className={`${classes.monthStatusCell} ${sep.trim()}`}
                      >
                        <div className={classes.cellSkeleton} />
                      </td>
                    );
                  }
                  const color = stageFlatColor(state);
                  const handleClick = () => {
                    if (typeof onOpenMonthProgress === "function") {
                      onOpenMonthProgress(mIdx);
                    }
                  };
                  const handleEnter = (e) => {
                    const hint = t(
                      "inputs_v3.overview.click_open_progress",
                      "clique para abrir o progresso da modelagem",
                    );
                    showTooltip(
                      e,
                      `<strong>${monthLong(month.month)} · ${month.year}</strong><br/>${monthStateLabel(state, month.reason || null, t)}<br/><span style="opacity:0.7">${hint}</span>`,
                    );
                  };
                  return (
                    <td
                      key={`mo-st-${mIdx}`}
                      className={`${classes.monthStatusCell} ${sep.trim()}`}
                    >
                      <div
                        className={classes.cell}
                        style={{ background: color }}
                        onClick={handleClick}
                        onMouseEnter={handleEnter}
                        onMouseMove={moveTooltip}
                        onMouseLeave={hideTooltip}
                      />
                    </td>
                  );
                }),
              )}
            </tr>
            {filteredGroups.map((g) => {
              const gName =
                (g.group_name && g.group_name[lang]) ||
                g.group_name?.pt ||
                g.group_name ||
                "";
              return (
                <React.Fragment key={gName}>
                  <tr className={`ov-group-row ${classes.groupRow}`}>
                    <td colSpan="2" className="ov-group-left">
                      {gName}
                      <span className={classes.groupCount}>
                        {t("inputs_v3.overview.inputs_count", {
                          count: g.inputs.length,
                          defaultValue:
                            g.inputs.length > 1
                              ? `${g.inputs.length} inputs`
                              : `${g.inputs.length} input`,
                        })}
                      </span>
                    </td>
                    <td colSpan={totalCols - 2} className="ov-group-right" />
                  </tr>
                  {g.inputs.map((input) => {
                    const name = displayName(input);
                    const respList = inputResponsibles(input);
                    // CSV de todos os donos. Ellipsis na CSS trunca
                    // quando estoura ``COL_RESP_WIDTH``; ``title``
                    // mostra a lista integral no hover nativo.
                    const respNames =
                      respList.length > 0
                        ? respList.map((r) => r.name).join(", ")
                        : "—";
                    // Rev. 2026-05-26: marker badges agora refletem
                    // 1:1 a legenda da visao detalhada (InputBoxV3) —
                    // antes so renderizavamos UM badge ``Repeat``
                    // para todos os "reusable", o que escondia a
                    // distincao entre singleton, semi_fixed,
                    // carry_forward e yearly. Cada flag agora vem
                    // com seu icone canonico e tooltip i18n.
                    const isMineInput = !!input?.is_mine;
                    // Rev. 2026-05-28 — ``semi_fixed`` removido; o badge
                    // "Repeat" agora deriva da flag ``reuse_auto`` para
                    // inputs ``monthly``.
                    const showReuseMarker = !!(
                      input?.volatility === "monthly" && input?.reuse_auto
                    );
                    const isSingletonInput = isSingleton(input?.volatility);
                    const isCarryForwardInput = isCarryForward(
                      input?.volatility,
                    );
                    const isYearlyInput = isYearly(input?.volatility);
                    const isOptionalInput = !!input?.optional;
                    return (
                      <tr key={input.meta_name || name}>
                        {/* Coluna do nome do input — NAO clicavel (a
                            interacao acontece nas caixas de status por
                            mes). Sem ``title`` para nao conflitar com
                            o Tooltip dos badges de marker. */}
                        <td className="cell-input">
                          <span className={classes.inputNameWrap}>
                            <span className={classes.inputNameText}>
                              {name}
                            </span>
                            {isMineInput && (
                              <Tooltip
                                title={t(
                                  "modeling_inputs_v3.legend.marker_mine",
                                )}
                                arrow
                              >
                                <span className={classes.reuseBadge}>
                                  <PersonOutline />
                                </span>
                              </Tooltip>
                            )}
                            {showReuseMarker && (
                              <Tooltip
                                title={t(
                                  "modeling_inputs_v3.legend.marker_reuse_auto",
                                )}
                                arrow
                              >
                                <span className={classes.reuseBadge}>
                                  <Repeat />
                                </span>
                              </Tooltip>
                            )}
                            {isSingletonInput && (
                              <Tooltip
                                title={t(
                                  "modeling_inputs_v3.legend.marker_singleton",
                                )}
                                arrow
                              >
                                <span className={classes.reuseBadge}>
                                  <FlashOn />
                                </span>
                              </Tooltip>
                            )}
                            {isCarryForwardInput && (
                              <Tooltip
                                title={t(
                                  "modeling_inputs_v3.legend.marker_carry_forward",
                                )}
                                arrow
                              >
                                <span className={classes.reuseBadge}>
                                  <TrendingFlat />
                                </span>
                              </Tooltip>
                            )}
                            {isYearlyInput && (
                              <Tooltip
                                title={t(
                                  "modeling_inputs_v3.legend.marker_yearly",
                                )}
                                arrow
                              >
                                <span className={classes.reuseBadge}>
                                  <Event />
                                </span>
                              </Tooltip>
                            )}
                            {isOptionalInput && (
                              <Tooltip
                                title={t(
                                  "modeling_inputs_v3.legend.marker_optional",
                                )}
                                arrow
                              >
                                <span className={classes.reuseBadge}>
                                  <HelpOutline />
                                </span>
                              </Tooltip>
                            )}
                          </span>
                        </td>
                        <td className="cell-resp">
                          <span className={classes.resp}>
                            <span
                              className={classes.respName}
                              title={respNames}
                            >
                              {respNames}
                            </span>
                          </span>
                        </td>
                        {grouped.map((y, yIdx) =>
                          y.items.map(({ month, idx: mIdx }, j) => {
                            const sep =
                              yIdx > 0 && j === 0 ? " year-sep-m" : "";
                            // Loading: skeleton shimmer, sem cor real,
                            // sem tooltip, sem click. Espera o backend.
                            if (isLoadingInputs) {
                              return (
                                <td key={`c-${mIdx}`} className={sep.trim()}>
                                  <div className={classes.cellSkeleton} />
                                </td>
                              );
                            }
                            const meta = input?.meta_name;
                            const bucket = meta
                              ? stateBucketByCell.get(
                                  `${month.year}-${month.month}-${meta}`,
                                )
                              : undefined;
                            // Fallback quando backend ja respondeu mas
                            // nao trouxe esta celula (input recem-
                            // adicionado, periodo fora da janela, etc).
                            // Cinza neutro — JAMAIS mock.
                            const effectiveBucket = bucket || "not_sent";
                            // ``BACKGROUNDS`` (gradiente quando o
                            // bucket exige) em vez de ``COLORS``
                            // (sempre solido). Para pending_decision
                            // pinta o hachurado warning+danger.
                            const cellColor =
                              INPUT_STATE_BACKGROUNDS[effectiveBucket];
                            const cellLabel = inputStateLabel(
                              effectiveBucket,
                              t,
                            );
                            const moreHint = t(
                              "inputs_v3.overview.click_more_details",
                              "clique para ver mais detalhes",
                            );
                            const tooltipHtml = `<strong>${name}</strong><br/>${monthLong(month.month)} · ${month.year}<br/>${cellLabel}<br/><span style="opacity:0.7">${moreHint}</span>`;
                            return (
                              <td key={`c-${mIdx}`} className={sep.trim()}>
                                <div
                                  className={`${classes.cell} ${
                                    effectiveBucket === "processing"
                                      ? classes.cellProcessing
                                      : ""
                                  }`}
                                  style={
                                    effectiveBucket === "processing"
                                      ? undefined
                                      : { background: cellColor }
                                  }
                                  onMouseEnter={(e) =>
                                    showTooltip(e, tooltipHtml)
                                  }
                                  onMouseMove={moveTooltip}
                                  onMouseLeave={hideTooltip}
                                  onClick={() => {
                                    onOpenInput?.(input, mIdx);
                                  }}
                                />
                              </td>
                            );
                          }),
                        )}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={classes.tooltip} ref={tooltipRef} />
    </div>
  );
}

OverviewV3.propTypes = {
  inputsStatus: PropTypes.array.isRequired,
  monthlyStatus: PropTypes.array.isRequired,
  selectedMonth: PropTypes.number,
  onOpenInput: PropTypes.func,
  // Click no status do mes (1a linha da tabela) -> abre o
  // ProgressDrawerV3 daquele mes. Recebe o ``monthIdx`` (alinhado
  // ao indice em ``monthlyStatus``).
  onOpenMonthProgress: PropTypes.func,
  selectStyles: PropTypes.object,
  // Lista vinda de ``progress_overview.inputs_state_by_period`` com
  // o estado de cada par (input, year, month). ``undefined`` indica
  // que o backend ainda nao respondeu — a UI mostra skeletons.
  // Array vazio ou cell sem entrada -> ``not_sent`` cinza neutro.
  inputsStateByPeriod: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
      meta_name: PropTypes.string.isRequired,
      attention_label: PropTypes.string,
      control_state: PropTypes.string,
      is_permanent_for_month: PropTypes.bool,
      cross_linking_state: PropTypes.string,
    }),
  ),
};

export default OverviewV3;
